import type { CommentDto } from "api/types";
import { fieldLimits } from "config";
import React, { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatLastModified } from "utilities";

import type { CommentItemProps } from "./commentItem.types";
import ViewIcon from "./ViewIcon";

import styles from "./CommentItem.module.css";

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

const CommentItem = ({
	comment,
	isReply,
	canReply,
	canModerate,
	shareUrl,
	onToggleLike,
	onPostReply,
	onEditComment,
	onDeleteComment
}: CommentItemProps) => {
	const { t } = useTranslation("homePage");
	const [isReplyOpen, setIsReplyOpen] = useState(false);
	const [replyBody, setReplyBody] = useState("");
	const [isPostingReply, setIsPostingReply] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editBody, setEditBody] = useState("");
	const [isSavingEdit, setIsSavingEdit] = useState(false);
	const [isLinkCopied, setIsLinkCopied] = useState(false);
	const copiedTimer = useRef<number | undefined>(undefined);

	useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

	const onLikeClick = useCallback((): void => onToggleLike(comment), [comment, onToggleLike]);
	const toggleReply = useCallback((): void => setIsReplyOpen((open: boolean) => !open), []);
	const onReplyChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>): void => setReplyBody(event.target.value),
		[]
	);
	const onDeleteClick = useCallback((): void => onDeleteComment(comment), [comment, onDeleteComment]);
	const startEditing = useCallback((): void => {
		setEditBody(comment.body);
		setIsEditing(true);
	}, [comment.body]);
	const cancelEditing = useCallback((): void => setIsEditing(false), []);
	const onEditChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>): void => setEditBody(event.target.value),
		[]
	);

	const onSaveEdit = useCallback(async (): Promise<void> => {
		const body = editBody.trim();
		if (!body || isSavingEdit) {
			return;
		}

		setIsSavingEdit(true);
		const saved = await onEditComment(comment, body);
		setIsSavingEdit(false);
		if (saved) {
			setIsEditing(false);
		}
	}, [comment, editBody, isSavingEdit, onEditComment]);

	const onShareClick = useCallback(async (): Promise<void> => {
		try {
			await navigator.clipboard.writeText(shareUrl);
			setIsLinkCopied(true);
			window.clearTimeout(copiedTimer.current);
			copiedTimer.current = window.setTimeout(() => setIsLinkCopied(false), 2000);
		} catch {
			// Clipboard access denied; leave the label unchanged.
		}
	}, [shareUrl]);

	const onSubmitReply = useCallback(async (): Promise<void> => {
		const body = replyBody.trim();
		if (!body || isPostingReply || !onPostReply) {
			return;
		}

		setIsPostingReply(true);
		const posted = await onPostReply(comment.id, body);
		setIsPostingReply(false);
		if (posted) {
			setReplyBody("");
			setIsReplyOpen(false);
		}
	}, [comment.id, isPostingReply, onPostReply, replyBody]);

	return (
		<div className={isReply ? styles.commentReply : styles.comment}>
			<span className={styles.commentAvatar}>
				{comment.authorImageUrl ? (
					<img alt="" referrerPolicy="no-referrer" src={comment.authorImageUrl} />
				) : (
					getInitials(comment.authorName)
				)}
			</span>
			<div className={styles.commentMain}>
				<div className={styles.commentHead}>
					<span className={styles.commentAuthor}>{comment.authorName}</span>
					{comment.isTreeOwner && <span className={styles.ownerBadge}>{t("view.ownerBadge")}</span>}
					<span className={styles.commentTime}>{formatLastModified(comment.createdDate)}</span>
					{comment.isEdited && <span className={styles.editedLabel}>{t("view.edited")}</span>}
				</div>

				{isEditing ? (
					<div className={styles.replyComposer}>
						<textarea
							aria-label={t("view.commentPlaceholder")}
							maxLength={fieldLimits.comment}
							onChange={onEditChange}
							value={editBody}
						/>
						<div className={styles.replyComposerActions}>
							<button className={styles.cancelReplyButton} onClick={cancelEditing} type="button">
								{t("view.cancel")}
							</button>
							<button
								className={styles.postReplyButton}
								disabled={isSavingEdit || editBody.trim().length === 0}
								onClick={onSaveEdit}
								type="button">
								<span>{t("view.save")}</span>
							</button>
						</div>
					</div>
				) : (
					<p className={styles.commentText}>{comment.body}</p>
				)}

				<div className={styles.commentActions}>
					<button
						aria-label={comment.isLikedByMe ? t("view.unlike") : t("view.like")}
						aria-pressed={comment.isLikedByMe}
						className={comment.isLikedByMe ? styles.likedButton : styles.actionButton}
						onClick={onLikeClick}
						type="button">
						<ViewIcon name="heart" />
						{comment.likeCount > 0 && <span className={styles.likeCount}>{comment.likeCount}</span>}
					</button>
					{!isReply && canReply && onPostReply && (
						<button className={styles.actionButton} onClick={toggleReply} type="button">
							<ViewIcon name="comment" />
							<span>{t("view.reply")}</span>
						</button>
					)}
					<button className={styles.actionButton} onClick={onShareClick} type="button">
						<ViewIcon name="share" />
						<span>{isLinkCopied ? t("view.linkCopied") : t("view.share")}</span>
					</button>
					{comment.isAuthor && !isEditing && (
						<button className={styles.actionButton} onClick={startEditing} type="button">
							<ViewIcon name="pencil" />
							<span>{t("view.edit")}</span>
						</button>
					)}
					{(comment.isAuthor || canModerate) && (
						<button className={styles.dangerButton} onClick={onDeleteClick} type="button">
							<ViewIcon name="close" />
							<span>{t("view.delete")}</span>
						</button>
					)}
				</div>

				{isReplyOpen && (
					<div className={styles.replyComposer}>
						<textarea
							aria-label={t("view.replyPlaceholder")}
							maxLength={fieldLimits.comment}
							onChange={onReplyChange}
							placeholder={t("view.replyPlaceholder")}
							value={replyBody}
						/>
						<div className={styles.replyComposerActions}>
							<button className={styles.cancelReplyButton} onClick={toggleReply} type="button">
								{t("view.cancel")}
							</button>
							<button
								className={styles.postReplyButton}
								disabled={isPostingReply || replyBody.trim().length === 0}
								onClick={onSubmitReply}
								type="button">
								<ViewIcon name="send" />
								<span>{t("view.reply")}</span>
							</button>
						</div>
					</div>
				)}

				{comment.replies.length > 0 && (
					<div className={styles.replyList}>
						{comment.replies.map((reply: CommentDto) => (
							<CommentItem
								key={reply.id}
								canModerate={canModerate}
								canReply={false}
								comment={reply}
								onDeleteComment={onDeleteComment}
								onEditComment={onEditComment}
								onToggleLike={onToggleLike}
								shareUrl={shareUrl}
								isReply
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default CommentItem;
